import { AppBar, Box, Button, Grid, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EmptyList,
	EnhancedSnackbar,
	RouterLinkButton,
} from "@websential/cosmic-v20";
import {
	CLIENT_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { IResponse, IServerError } from "../../models/common";
import { Link, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {  formatDate, useNavigateToLogout } from "../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedDrawerMenu,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminClientRoutesComponent } from "../RoleBasedLeftMenu/routes/AdminClientRoutes";
import { ClientRoutesComponent } from "../RoleBasedLeftMenu/routes/ClientRoutes";
import { CountryService } from "../../services/common/CountryService";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IWSIBCategory } from "../../models/WSIBCategory";
import { PrimeDataGrid } from "../PrimeDataGrid";
import { WSIBCategoryListDataGridTable } from "./WSIBCategoryListDataGridTable/WSIBCategoryListDataGridTable";
import { WSIBCategoryListHeader } from "./WSIBCategoryListHeader";
import { WSIBCategoryService } from "../../services/WSIBCategoryService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";

export interface IWSIBCategoryList { }
const WSIB_CATEGORY_LIST_QUERY_KEY = "wsib-category-list";

const useWSIBCategory = (
	runGetListAPICall: boolean,
	label: string,
	description: string,
	provinceId: string,
	effectiveDate: String | null,
	endDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [

			WSIB_CATEGORY_LIST_QUERY_KEY,
			runGetListAPICall,
			label,
			description,
			provinceId,
			effectiveDate,
			endDate,
			params,
			navigateToLogout
		],
		queryFn: (data) => {

			return WSIBCategoryService.getWSIBCategory(
				runGetListAPICall,
				label,
				description,
				provinceId,
				effectiveDate,
				endDate,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetCountryDDL = () =>
	useQuery({
		queryKey: ["country-ddl"],
		queryFn: (data) => {
			return CountryService.getCountryDDL();
		},
		refetchOnWindowFocus: false,
	});

export const WSIBCategoryList: React.FC<IWSIBCategoryList> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();
	const { AdminClientRoutes } = AdminClientRoutesComponent();
	const { ClientRoutes } = ClientRoutesComponent();

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoadingProvinceDDL, setIsLoadingProvinceDDL] = useState(false);
	const [selectedRowsValues, setSelectedRowsValues] = useState([]);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "label",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const { id } = useSelector((state: IAppState) => state.accountant);

	const [openDialog, setDialogOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState([]);
	const [statusValue, setStatusValue] = useState("");
	const [apiAction, setApiAction] = useState("");
	const [emptyList, setEmptyList] = useState(false);
	const [searchText, setSearchText] = useState({});
	const [zeroWSIBCategory, setzeroWSIBCategory] = useState(true);
	const [wsibCategoryListUpdatedItems, setWSIBCategoryListUpdatedItems] =
		useState<any>(undefined);

	// for delete row
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [wsibCategoryId, setWSIBCategoryId] = useState<any>(0);

	// Bulk Delete
	const [bulkDelete, setBulkDelete] = useState<boolean>(false);
	const [bulkWSIBCategoryId, setBulkWSIBCategoryId] = useState<any>({});
	const [bulkWSIBCategoryDelete, setBulkWSIBCategoryDelete] =
		useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "label",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);


	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const { isPending: isPendingCountryDDL, data: countryDDL } = useGetCountryDDL();


	const label = formMethods.getValues("label");
	const description = formMethods.getValues("description");
	const provinceId = formMethods.getValues("provinceId");
	const effectiveDate = formMethods.getValues("effectiveDate");
	const endDate = formMethods.getValues("endDate");

	const { isPending, data } = useWSIBCategory(
		runGetListAPICall,
		label,
		description,
		provinceId,
		formatDate(effectiveDate),
		formatDate(endDate),
		queryParams,
		navigateToLogout
	);
	const wsibCategoryList: any | null = data;
	

	useEffect(() => {
		if (wsibCategoryList) {
			setWSIBCategoryListUpdatedItems(wsibCategoryList)
		}
	}, [wsibCategoryList])

	// on apply button click api call wsib category list 
	const wsibCategoryListDataAPICall = (

		label: string,
		description: string,
		provinceId: string,

		effectiveDate: String,
		endDate: string,
	) => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const response: any = await WSIBCategoryService.getWSIBCategory(
					true,

					label,
					description,
					provinceId,

					formatDate(effectiveDate),
					formatDate(endDate),
					queryParams,
					navigateToLogout
				);

				if (response) {
					setWSIBCategoryListUpdatedItems(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
		
		wsibCategoryListDataAPICall(

			values.label,
			values.description,
			values.provinceId,

			values.effectiveDate,
			values.endDate,
		);
	};


	const onSearchButtonClicked = () => {
		setQueryParams(searchText);
		setzeroWSIBCategory(false);
	};

	const onRowEditClicked = useCallback((params: IWSIBCategory) => {
		let path = `/workplace-safety/category/setup/${params.id}`;
		navigate(path);
	}, []);

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const callAction = () => {
		if (apiAction === "delete") {
			alert(`Delete row with ID: ${selectedIds} has been clicked!`);
			// TODO api call for delete
		} else if (apiAction === "updateStatus") {
			alert(`action need to trigger : ${apiAction}`);
			// TODO api call for update status
		}
		setDialogOpen(false);
	};

	// For Delete Row
	const deleteWSIBCategoryField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				wsibCategoryId: wsibCategoryId,
				navigateToLogout: navigateToLogout,
			};
			deleteWSIBCategoryById(delPayload);
			setDialogOpen(false);
		}
	};

	const onRowDeleteClicked = useCallback((params: IWSIBCategory) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setApiAction("delete");
		setWSIBCategoryId(params.id);
	}, []);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteWSIBCategoryById } = useMutation({
		mutationFn: WSIBCategoryService.delWSIBCategory,

		onSuccess: (res: string | IServerError | IResponse | any) => {

			if (typeof (res) === 'string') {
				// remove the cache of the Year fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: res,
					severity: "success",
				});

				return queryClient.invalidateQueries({
					queryKey: [WSIB_CATEGORY_LIST_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong.",
					severity: res.status === false ? "error" : "success",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		setStatusValue(event.target.value as string);
		setApiAction("updateStatus");
		setDialogOpen(true);
	};

	// const onKeyFieldClickHandler = (clickedRow: any) => {
	// dispatch(setSelectedWSIBCategory(clickedRow));
	// dispatch(setSelectedWSIBCategoryName(clickedRow));
	// };

	//bulk delete

	const { isPending: isBulkDeleting, mutate: deleteBulkWSIBCategoryes } =
		useMutation({
			mutationFn: WSIBCategoryService.delBulkWSIBCategoryes,
			onSuccess: (res: any | string | IServerError | IResponse) => {
				if (typeof res === "object") {
					// remove the cache of the Year fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong.",
						severity: res.status === false ? "error" : "success",
					});

					setBulkWSIBCategoryDelete(false);
					return queryClient.invalidateQueries({
						queryKey: [WSIB_CATEGORY_LIST_QUERY_KEY],
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				setBulkWSIBCategoryDelete(false);
			},
			onError: (err: any) => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
				setBulkWSIBCategoryDelete(false);
			},
		});

	// For Bulk Delete
	const deleteBulkWSIBCategoryField = () => {
		if (bulkDelete) {
			setBulkDelete(false);
			let payload = {
				wsibCategoryIds: bulkWSIBCategoryId,
				navigateToLogout: navigateToLogout,
			};
			deleteBulkWSIBCategoryes(payload);
			setDialogOpen(false);
		}
	};

	const onRowMultipleDeleteClicked = (params: any) => {

		let bulkDeletePayload = {
			wsibCategoryIds: { wsibCategoryIds: params },
		};
		setDialogOpen(true);
		setBulkDelete(true);
		setApiAction("delete");
		setBulkWSIBCategoryId(bulkDeletePayload);
		setBulkWSIBCategoryDelete(true);
	};

	const selectionToolbar = useCallback((selectedItems: any) => {
		setSelectedIds(selectedItems);

		// if (selectedItems.length) {
		// 	return (
		// 		<Box sx={{ marginTop: theme.spacing(1) }}>
		// 			<Button
		// 				onClick={() => onRowMultipleDeleteClicked(selectedItems)}
		// 				variant="contained"
		// 				color="error"
		// 			>
		// 				Delete
		// 			</Button>
		// 		</Box>
		// 	);
		// }
		return (
			// <Box sx={{ marginTop: theme.spacing(1) }}>
			// 	<RouterLinkButton
			// 		to="/company/wsibCategory/setup"
			// 		text="Add WSIBCategory"
			// 	/>
			// </Box>
			<></>
		);
	}, []);

	const upadtedWSIBCategoryListItems = useMemo(() => {
		return (
			wsibCategoryListUpdatedItems &&
			wsibCategoryListUpdatedItems.items &&
			wsibCategoryListUpdatedItems?.items.map((item: any) => ({
				...item,

			}))
		);
	}, [wsibCategoryListUpdatedItems]);

	

	const expandedLabels = ["Workplace Safety", "Category"];
	const expandedWSIBClategoryListOrSetupItem = "List"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Category" &&
				item.label === expandedWSIBClategoryListOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === "Category" && item.label === expandedWSIBClategoryListOrSetupItem && "/workplace-safety/category/list",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: CLIENT_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: false }));


		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_wsibCategory_list.png";

	return (
		// wsibCategoryList &&
		// 	wsibCategoryList.items &&
		// 	wsibCategoryList.items.length === 0 &&
		// 	zeroWSIBCategory === true ? (
		// 	<EmptyList
		// 		showImage={true}
		// 		imagePath={image}
		// 		heading={`There's nothing! Add WSIBCategory to proceed`}
		// 		content={`You can add WSIBCategory by directly clicking on add WSIBCategory button.`}
		// 		testId={"wsibCategory-empty-list"}
		// 		showAddButton={true}
		// 		addButtonLabel={"Add WSIBCategory"}
		// 		navigateToAdd={"/company/wsibCategory/setup"}
		// 		showImportButton={false}
		// 		showImportXmlButton={false}
		// 	/>
		// ) : (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Category List"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<WSIBCategoryListHeader

						countryDDL={countryDDL && countryDDL}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
						setIsLoadingProvinceDDL={(value: boolean) => setIsLoadingProvinceDDL(value)}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending || isLoading || isLoadingProvinceDDL || isPendingCountryDDL
					}
					testId="wsib-category-list"
				/>

				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={
						bulkWSIBCategoryDelete === true
							? deleteBulkWSIBCategoryField
							: deleteWSIBCategoryField
					}
					cancelActionHandler={onCloseHandler}
					testId="wsib-category-list-confirmation-dialog"
				/>
				<WSIBCategoryListDataGridTable
					columns={wsibCategoryListUpdatedItems ? wsibCategoryListUpdatedItems.headerColumns : wsibCategoryList?.headerColumns ?? []}
					rows={
						wsibCategoryListUpdatedItems && upadtedWSIBCategoryListItems
							? upadtedWSIBCategoryListItems
							: wsibCategoryList?.items ?? []
						// wsibCategoryList?.items ?? []
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={true}
					onRowEditClicked={onRowEditClicked}
					onRowDeleteClicked={onRowDeleteClicked}
					isLoading={isPending || isDeleting || isBulkDeleting}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						wsibCategoryListUpdatedItems && wsibCategoryListUpdatedItems.total ? wsibCategoryListUpdatedItems.total : 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					selectionToolbar={selectionToolbar}
					selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
					// searchFieldPlaceholder={"Search by Name..."}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroWSIBCategory(value)
					}
				// pageTitle="WSIB Category List"
				/>


				{wsibCategoryList ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}

			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
